import { useState } from 'react'
import { describeError } from '@/lib/auth'
import { REPORT_CATEGORIES, reportApi, type ReportCategory, type ReportTarget } from '@/lib/reportApi'
import { cx } from '@/lib/cx'
import { Modal } from './settings/Modal'
import styles from './ReportModal.module.css'

interface Props {
  target: ReportTarget
  onClose: () => void
}

/** Жалоба на сообщение, пользователя или сервер: категория, комментарий, подтверждение. */
export function ReportModal({ target, onClose }: Props) {
  const [category, setCategory] = useState<ReportCategory | null>(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ existing: boolean } | null>(null)

  const submit = async () => {
    if (!category) return
    setBusy(true)
    setError(null)
    try {
      const res = await reportApi.create(target, category, text.trim())
      setDone({ existing: Boolean(res.existing) })
    } catch (err) {
      setError(describeError(err).message)
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <Modal title="Жалоба отправлена" onClose={onClose}>
        <p className={styles.text}>
          {done.existing
            ? 'Вы уже отправляли жалобу на это, она в очереди модерации.'
            : 'Спасибо. Команда модерации рассмотрит жалобу в течение 72 часов, тяжёлые категории — в течение 24 часов.'}
        </p>
        <div className={styles.buttons}>
          <button type="button" className={styles.primary} onClick={onClose}>Понятно</button>
        </div>
      </Modal>
    )
  }

  return (
    <Modal title={`Пожаловаться: ${target.label}`} onClose={onClose} width={480}>
      <p className={styles.text}>Что нарушено? Жалобу увидят только модераторы платформы, автор о ней не узнает.</p>
      <div className={styles.categories} role="radiogroup" aria-label="Категория жалобы">
        {REPORT_CATEGORIES.map((c) => (
          <label key={c.value} className={cx(styles.category, category === c.value && styles.categoryOn)}>
            <input type="radio" name="report-category" value={c.value} checked={category === c.value} onChange={() => setCategory(c.value)} />
            <span className={styles.categoryLabel}>{c.label}</span>
            {c.hint ? <span className={styles.hint}>{c.hint}</span> : null}
          </label>
        ))}
      </div>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>Подробности (необязательно)</span>
        <textarea className={styles.textarea} rows={3} maxLength={1000} value={text} placeholder="Что именно произошло" onChange={(e) => setText(e.target.value)} />
      </label>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}
      <div className={styles.buttons}>
        <button type="button" className={styles.cancel} onClick={onClose}>Отмена</button>
        <button type="button" className={styles.primary} disabled={!category || busy} onClick={() => void submit()}>{busy ? 'Отправляем…' : 'Отправить жалобу'}</button>
      </div>
    </Modal>
  )
}
